import { useEffect, useState } from "react";
import { Button } from "@oc-labs/wt-storybook";
import { Plus, Shield, ShieldCheck } from "lucide-react";
import useModal from "../../../hooks/useModal";
import useSSO from "../../../hooks/useSSO";
import toast from "react-hot-toast";
import UpsertSSOProviderModal from "../../../components/modals/sso/UpsertSSOProviderModal";
import DeleteModal from "../../../components/modals/DeleteModal";

export default function DashboardSSO() {
  const { open: openUpsertModal } = useModal("upsert-sso-provider-modal");
  const { open: openDeleteModal } = useModal("delete-modal");
  const { listProviders, deleteProvider } = useSSO();
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = () =>
    listProviders()
      .then((res) => setProviders(res))
      .catch((err) => toast.error(err.message || "Failed to load SSO providers"));

  const handleDeleteProvider = (providerId) => {
    openDeleteModal({
      title: "Delete SSO Provider",
      text: `Are you sure you want to delete the provider "${providerId}"? Users authenticating through this provider will no longer be able to sign in.`,
      onDelete: async () => {
        try {
          await deleteProvider(providerId);
          toast.success("SSO provider deleted successfully");
          loadProviders();
        } catch (error) {
          toast.error(error.message || "Failed to delete SSO provider");
        }
      },
    });
  };

  return (
    <>
      <UpsertSSOProviderModal onCreated={loadProviders} />
      <DeleteModal />
      <div className="2xl:p-6 p-3">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="2xl:text-xl text-base font-medium">SSO Providers</h2>
            <p className="text-gray-500 text-sm mt-1">
              Configure Single Sign-On providers for federated authentication.
            </p>
          </div>
          <div className="flex">
            <Button
              type="secondary"
              size="small"
              onClick={() => openUpsertModal()}
              icon={<Plus size={16} />}
              className="whitespace-nowrap"
            >
              Add Provider
            </Button>
          </div>
        </div>

        {providers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg shadow">
            <Shield className="text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No SSO providers configured</h3>
            <p className="text-gray-500 text-sm mb-6">
              Add an OIDC provider to enable Single Sign-On for your users.
            </p>
            <Button
              type="primary"
              size="small"
              onClick={() => openUpsertModal()}
              icon={<Plus size={16} />}
            >
              Add Provider
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {providers.map((provider) => (
              <div
                key={provider.providerId}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                {/* Provider Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <ShieldCheck className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        {provider.providerId}
                      </h3>
                      <p className="text-sm text-gray-500">{provider.issuer}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => openUpsertModal(provider)}
                      size="small"
                      type="primary"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteProvider(provider.providerId)}
                      size="small"
                      type="danger"
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {/* Provider Details Grid */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                      Domain
                    </p>
                    <p className="text-sm text-gray-900">{provider.domain}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                      PKCE
                    </p>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        provider.oidcConfig?.pkce
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {provider.oidcConfig?.pkce ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                      Client ID
                    </p>
                    <p className="text-sm text-gray-900 font-mono truncate">
                      {provider.oidcConfig?.clientIdLastFour
                        ? `••••${provider.oidcConfig.clientIdLastFour}`
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                      Scopes
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(provider.oidcConfig?.scopes || []).map((scope) => (
                        <span
                          key={scope}
                          className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
